import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";

const mediaFiles = [
  "gif_one.gif",
  "gif_two.gif",
  "gif_three.gif",
  "gif_four.gif",
  "SnapVid.Net__pQFMsKqDDJWzpKJ_720p.mp4",
];

const media = mediaFiles.map((file) => ({
  src: `${process.env.PUBLIC_URL}/gifs/${file}`,
  type: file.toLowerCase().endsWith(".mp4") ? "video" : "image",
}));

function Media({ item, alt, className, style, controls = false, onEnded }) {
  if (item.type === "video") {
    return (
      <video
        src={item.src}
        className={className}
        style={style}
        autoPlay
        muted={!controls}
        loop={!controls && !onEnded}
        playsInline
        controls={controls}
        onEnded={onEnded}
      />
    );
  }
  return <img src={item.src} alt={alt} className={className} style={style} />;
}

export default function App() {
  const [goonerMode, setGoonerMode] = useState(false);
  const [showIntro, setShowIntro] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(null);

  const enterGooner = () => {
    setShowIntro(true);
    setTimeout(() => {
      setShowIntro(false);
      setGoonerMode(true);
    }, 2000); // 2 seconds intro
  };

  return (
    <div className="min-h-screen bg-black text-white relative">
      {!goonerMode ? (
        <div className="p-8 space-y-10">
          <header className="flex items-center justify-between">
            <h1 className="text-3xl font-bold">🔥 Pleasure Portal</h1>
            <button
              onClick={enterGooner}
              className="bg-pink-600 hover:bg-pink-700 transition px-6 py-2 rounded-2xl text-lg shadow-lg"
            >
              Enter Gooner Mode
            </button>
          </header>

          <section className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {media.map((item, i) => (
              <button
                key={item.src}
                onClick={() => setViewerIndex(i)}
                className="relative block rounded-xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-pink-500 hover:opacity-90 transition"
              >
                <Media
                  item={item}
                  alt={`Item ${i + 1}`}
                  className="w-full h-64 object-cover"
                />
                {item.type === "video" && (
                  <span className="absolute bottom-2 right-2 bg-black/70 text-xs px-2 py-1 rounded">
                    ▶ Video
                  </span>
                )}
              </button>
            ))}
          </section>
        </div>
      ) : (
        <GoonerMode onExit={() => setGoonerMode(false)} items={media} />
      )}

      <AnimatePresence>
        {viewerIndex !== null && (
          <Viewer
            items={media}
            index={viewerIndex}
            setIndex={setViewerIndex}
            onClose={() => setViewerIndex(null)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showIntro && (
          <motion.div
            className="fixed inset-0 flex items-center justify-center bg-black z-50"
            initial={{ opacity: 0, y: 50, scale: 0.8 }}
            animate={{
              opacity: 1,
              y: [50, -20, 0, -10, 0], // jump effect
              scale: [0.8, 1.1, 1, 1.1, 1],
            }}
            exit={{ opacity: 0 }}
            transition={{ duration: 2, ease: "easeInOut" }}
          >
            <h1
              style={{ fontFamily: "Impact, Arial Black, sans-serif" }}
              className="text-7xl text-purple-600 select-none drop-shadow-lg"
            >
              GOONER MODE
            </h1>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Viewer({ items, index, setIndex, onClose }) {
  const prev = useCallback(
    () => setIndex((i) => (i - 1 + items.length) % items.length),
    [items.length, setIndex]
  );
  const next = useCallback(
    () => setIndex((i) => (i + 1) % items.length),
    [items.length, setIndex]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, prev, next]);

  return (
    <motion.div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <div
        className="max-w-full max-h-full"
        onClick={(e) => e.stopPropagation()}
      >
        <Media
          key={items[index].src}
          item={items[index]}
          alt={`Item ${index + 1}`}
          className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg"
          controls
        />
      </div>

      <button
        onClick={(e) => {
          e.stopPropagation();
          prev();
        }}
        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 w-12 h-12 rounded-full text-2xl"
        aria-label="Previous"
      >
        ‹
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          next();
        }}
        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 w-12 h-12 rounded-full text-2xl"
        aria-label="Next"
      >
        ›
      </button>
      <button
        onClick={onClose}
        className="absolute top-4 right-4 bg-white text-black px-4 py-2 rounded-full text-sm hover:bg-gray-200"
      >
        Close
      </button>
      <span className="absolute bottom-4 text-sm text-gray-400">
        {index + 1} / {items.length}
      </span>
    </motion.div>
  );
}

function GoonerMode({ onExit, items }) {
  const [index, setIndex] = useState(0);
  const advance = useCallback(
    () => setIndex((i) => (i + 1) % items.length),
    [items.length]
  );
  const current = items[index];

  // Images rotate every 5s; videos advance when they finish playing.
  useEffect(() => {
    if (current.type === "video") return;
    const timeout = setTimeout(advance, 5000);
    return () => clearTimeout(timeout);
  }, [current, advance]);

  return (
    <div className="fixed inset-0 z-40 bg-black flex items-center justify-center p-4">
      <AnimatePresence mode="wait">
        <motion.div
          key={index}
          className="w-full h-full"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1 }}
        >
          <Media
            item={current}
            alt={`Item ${index + 1}`}
            className="w-full h-full object-contain rounded-md shadow-lg"
            style={{ transform: "scale(0.9)", borderRadius: "12px" }}
            onEnded={advance}
          />
        </motion.div>
      </AnimatePresence>
      <button
        onClick={onExit}
        className="absolute top-4 right-4 bg-white text-black px-4 py-2 rounded-full text-sm hover:bg-gray-200"
      >
        Exit
      </button>
    </div>
  );
}
